import React, { useEffect } from 'react';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { DashBoardParamList, DispatchParamList } from '../../../typings/Params';
import Filters from '../../Filter/FilterIndex/FilterIndex';
import FilterInput from '../../Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { useState } from 'react';
import TypeDropdown from './TypeDropdown';
import PriorityList from './PriorityList';
import StatusList from './StatusList';
import TagsList from './TagsList';
import ActingBodiesList from './ActingBodiesList';
import moment from 'moment';
import FilterDatePicker from '../../Filter/components/FilterDateInput';
import { View } from 'react-native';
import MyAppText from '../../MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import OccurrenceTypesDropdown from '../../dispatch/OccurrenceTypesDropdown';
import FinishReasonDropdown from '../../dispatch/FinishReasonDropdown';
import LocationSelector from '../../LocationSelector';
import { centralAPI } from '../../../services/central-api';
import { Slider } from '@miblanchard/react-native-slider';
import Map from '../../Map';
import { useAuth } from '../../../context/Auth';
import { OccurrencePriorities, OccurrenceSituation, TriggerType } from '../../../schemas/dispatch';
import FinishReasonSubCategoryDropdown from '../../dispatch/FinishReasonSubCategoryDropdown';
import ActingBodyUnitsList from './ActingBodyUnitsList';
import CameraTagsList from './CameraTagsList';
import CollapsibleFilter from './CollapsibleFilter';
import DispatchUnitNamesList from './DispatchUnitNamesList';
import FilterRadioList from './FilterRadioList';
import DispatchActingBodiesCommandsList from './DispatchActingBodiesCommandsList';
import DispatchGenericUserList from './DispatchGenericUserList';
import DispatchDisplacedReasonsList from './DispatchDisplacedReasonsList';
import DispatchRegionsList from './DispatchRegionsList';


interface Props {
    filters: DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences'];
    setFilters: React.Dispatch<React.SetStateAction<DispatchParamList['Occurrences'] | DashBoardParamList['DashboardOccurrences']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function OccurrencesFilter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<DispatchParamList, 'Occurrences'>>();
    const { styles, theme } = useStyles(styleSheet);

    const [selectedTriggerTypeFilter, setSelectedTriggerTypeFilter] = useState<(TriggerType | 'manual')[]>(filters.triggerTypeIds ?? []);
    const [selectedPriorityFilter, setSelectedPriorityFilter] = useState<OccurrencePriorities | 'all'>(filters.priority ?? 'all');
    const [selectedSituationFilter, setSelectedSituationFilter] = useState<OccurrenceSituation | 'all'>(filters.situation ?? 'all');
    const [selectedOccurrenceTypesFilter, setSelectedOccurrenceTypesFilter] = useState<number[]>(filters.occurrenceTypeId ?? []);
    const [selectedUnitNamesIds, setSelectedUnitNamesIds] = useState<number[]>(filters.unitNamesIds ?? []);
    const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>(filters.tags ?? []);
    const [selectedCameraTagsIds, setSelectedCameraTagsIds] = useState<number[]>(filters.cameraTags ?? []);
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(filters.actingBodyId ?? 0);
    const [selectedMapPolygonsIds, setSelectedMapPolygonsIds] = useState<number[]>(filters.mapPolygonIds ?? []);
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType || '');
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);

    const [selectedAssumedBy, setSelectedAssumedBy] = useState<number>(filters.assumedBy ?? 0);
    const [selectedTransferredBy, setSelectedTransferredBy] = useState<number>(filters.transferredBy ?? 0);
    const [selectedReceivedBy, setSelectedReceivedBy] = useState<number>(filters.receivedBy ?? 0);
    const [selectedFinishedBy, setSelectedFinishedBy] = useState<number>(filters.finishedBy ?? 0);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');
    const [actingBodyCommandIds, setActingBodyCommandIds] = useState<number[]>(filters.actingBodyCommandIds ?? []);
    const [withDispatch, setWithDispatch] = useState<boolean>(filters.withDispatch ?? false);
    const [withoutDispatch, setWithoutDispatch] = useState<boolean>(filters.withoutDispatch ?? false);
    const [withOccurrenceDocument, setWithOccurrenceDocument] = useState<boolean>(filters.withOccurrenceDocument ?? false);
    const [withoutOccurrenceDocument, setWithoutOccurrenceDocument] = useState<boolean>(filters.withoutOccurrenceDocument ?? false);
    const [selectedDisplaceReasonId, setSelectedDisplaceReasonId] = useState<number[]>(filters.displaceReasonIds ?? []);
    const [selectedFinishReasonCategoryIds, setSelectedFinishReasonCategoryIds] = useState<number[]>(filters.finishReasonCategoryIds ?? []);
    const [selectedRadius, setSelectedRadius] = useState<number>(filters.radius ?? 1);
    const [address, setAddress] = useState('');
    const [pin, setPin] = useState<LatLng | undefined>(filters.latitude && filters.longitude ? { lat: filters.latitude, lng: filters.longitude } : undefined);
    const [selectedFinishReasonSubCategoryIds, setSelectedFinishReasonSubCategoryIds] = useState<number[]>(filters.finishReasonSubCategoryIds ?? []);
    const [selectedActingBodyUnitIds, setSelectedActingBodyUnitIds] = useState<number[]>(filters.actingBodyUnitIds ?? []);
    const [policeReportFilter, setPoliceReportFilter] = useState(filters.policeReport ?? '');
    const [openFilter, setOpenFilter] = useState<string | null>(null);

    const totalActiveFilters = [
        selectedActingBodyId !== 0 ? 1 : 0,
        selectedUnitNamesIds.length > 0 ? 1 : 0,
        actingBodyCommandIds.length > 0 ? 1 : 0,
        beginInDateFilter ? 1 : 0,
        endInDateFilter ? 1 : 0,
        selectedPriorityFilter !== 'all' ? 1 : 0,
        selectedSituationFilter !== 'all' ? 1 : 0,
        selectedTagsIds.length > 0 ? 1 : 0,
        policeReportFilter ? 1 : 0,
        selectedActingBodyUnitIds.length > 0 ? 1 : 0,
        selectedDisplaceReasonId.length > 0 ? 1 : 0,
        selectedPolygonType ? 1 : 0,
        selectedMapPolygonsIds.length > 0 ? 1 : 0,
        pin ? 1 : 0,
        (withDispatch || withoutDispatch) ? 1 : 0,
        (withOccurrenceDocument || withoutOccurrenceDocument) ? 1 : 0,
        selectedTriggerTypeFilter.length > 0 ? 1 : 0,
        selectedOccurrenceTypesFilter.length > 0 ? 1 : 0,
        selectedCameraTagsIds.length > 0 ? 1 : 0,
        selectedAssumedBy !== 0 ? 1 : 0,
        selectedFinishedBy !== 0 ? 1 : 0,
        selectedReceivedBy !== 0 ? 1 : 0,
        selectedTransferredBy !== 0 ? 1 : 0,
        selectedFinishReasonCategoryIds.length > 0 ? 1 : 0,
        selectedFinishReasonSubCategoryIds.length > 0 ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    const toggleFilter = (key: string) => {
        setOpenFilter(prev => prev === key ? null : key);
    };

    const handleCleanFilter = () => {
        setOpenFilter(null);
        setTextFilter('');
        setSelectedOccurrenceTypesFilter([]);
        setSelectedActingBodyId(0);
        setSelectedMapPolygonsIds([]);
        setSelectedAssumedBy(0);
        setSelectedTransferredBy(0);
        setSelectedReceivedBy(0);
        setSelectedFinishedBy(0);
        setSelectedPriorityFilter('all');
        setSelectedSituationFilter('all');
        setSelectedTriggerTypeFilter([]);
        setSelectedUnitNamesIds([]);
        setSelectedTagsIds([]);
        setSelectedCameraTagsIds([]);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setActingBodyCommandIds([]);
        setWithDispatch(false);
        setWithoutDispatch(false);
        setWithOccurrenceDocument(false);
        setWithoutOccurrenceDocument(false);
        setSelectedDisplaceReasonId([]);
        setSelectedFinishReasonCategoryIds([]);
        setSelectedRadius(1);
        setAddress('');
        setPin(undefined);
        setSelectedPolygonType('');
        setSelectedFinishReasonSubCategoryIds([]);
        setSelectedActingBodyUnitIds([]);
        setPoliceReportFilter('');

    };

    async function fetchAddress(pin: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pin.lat, longitude: pin.lng });
        if (address) {
            setAddress(address);
        }
    }

    useEffect(() => {
        if (selectedFinishReasonCategoryIds.length !== 1) {
            setSelectedFinishReasonSubCategoryIds([]);
        }
    }, [selectedFinishReasonCategoryIds]);

    useEffect(() => {
        if (pin) {
            fetchAddress(pin);
        }
    }, [pin]);

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        setFilters({
            ...filters,
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 0,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            withoutOccurrenceDocument: withoutOccurrenceDocument ? withoutOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            finishReasonSubCategoryIds: selectedFinishReasonSubCategoryIds.length ? selectedFinishReasonSubCategoryIds : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
            cameraTags: selectedCameraTagsIds.length ? selectedCameraTagsIds : undefined,
        });

        navigation.setParams({
            situation: selectedSituationFilter !== 'all' ? selectedSituationFilter : undefined,
            priority: selectedPriorityFilter !== 'all' ? selectedPriorityFilter : undefined,
            triggerTypeIds: selectedTriggerTypeFilter.length ? selectedTriggerTypeFilter : undefined,
            occurrenceTypeId: selectedOccurrenceTypesFilter.length ? selectedOccurrenceTypesFilter : undefined,
            unitNamesIds: selectedUnitNamesIds.length ? selectedUnitNamesIds : undefined,
            textFilter: textFilter ? textFilter : undefined,
            page: 1,
            tags: selectedTagsIds.length ? selectedTagsIds : undefined,
            actingBodyId: selectedActingBodyId !== 0 ? selectedActingBodyId : undefined,
            mapPolygonIds: selectedMapPolygonsIds.length ? selectedMapPolygonsIds : undefined,
            polygonType: selectedPolygonType ? selectedPolygonType : undefined,
            assumedBy: selectedAssumedBy !== 0 ? selectedAssumedBy : undefined,
            transferredBy: selectedTransferredBy !== 0 ? selectedTransferredBy : undefined,
            receivedBy: selectedReceivedBy !== 0 ? selectedReceivedBy : undefined,
            finishedBy: selectedFinishedBy !== 0 ? selectedFinishedBy : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
            actingBodyCommandIds: actingBodyCommandIds.length ? actingBodyCommandIds : undefined,
            withDispatch: withDispatch ? withDispatch : undefined,
            withoutDispatch: withoutDispatch ? withoutDispatch : undefined,
            withOccurrenceDocument: withOccurrenceDocument ? withOccurrenceDocument : undefined,
            withoutOccurrenceDocument: withoutOccurrenceDocument ? withoutOccurrenceDocument : undefined,
            displaceReasonIds: selectedDisplaceReasonId.length ? selectedDisplaceReasonId : undefined,
            finishReasonCategoryIds: selectedFinishReasonCategoryIds.length ? selectedFinishReasonCategoryIds : undefined,
            latitude: pin ? pin.lat : undefined,
            longitude: pin ? pin.lng : undefined,
            radius: pin ? selectedRadius : undefined,
            actingBodyUnitIds: selectedActingBodyUnitIds.length ? selectedActingBodyUnitIds : undefined,
            policeReport: policeReportFilter ? policeReportFilter : undefined,
            cameraTags: selectedCameraTagsIds.length ? selectedCameraTagsIds : undefined,
        });

    };

    function getActingBodyId() {
        if (!authenticatedUser || !authenticatedUser.actingBodyUnit.actingBodyId) {
            return 0;
        }

        if (authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_occurrences) {
            return selectedActingBodyId;
        } else {
            return authenticatedUser.actingBodyUnit.actingBodyId;
        }
    }

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={!!selectedPolygonType && !selectedMapPolygonsIds.length}
            filterCount={totalActiveFilters}
        >
            <MyAppText style={styles.sectionTitle(0)}>
                {translate('organizational')}
            </MyAppText>
            {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences) &&
                <CollapsibleFilter
                    label={translate('actingBody')}
                    isOpen={openFilter === 'actingBody'}
                    onToggle={() => toggleFilter('actingBody')}
                    count={selectedActingBodyId !== 0 ? 1 : 0}
                >
                    <ActingBodiesList value={selectedActingBodyId} setValue={setSelectedActingBodyId} />
                </CollapsibleFilter>
            }

            <CollapsibleFilter
                label={translate('actingBodyUnit')}
                isOpen={openFilter === 'unitNames'}
                onToggle={() => toggleFilter('unitNames')}
                count={selectedActingBodyUnitIds.length}
            >
                <ActingBodyUnitsList value={selectedActingBodyUnitIds} setValue={setSelectedActingBodyUnitIds} actingBodyId={getActingBodyId()} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('command')}
                isOpen={openFilter === 'actingBodyCommands'}
                onToggle={() => toggleFilter('actingBodyCommands')}
                count={actingBodyCommandIds.length}
            >
                <DispatchActingBodiesCommandsList value={actingBodyCommandIds} actingBodyId={getActingBodyId()} setValue={setActingBodyCommandIds} />
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('occurrenceData')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('createdAt')}
                isOpen={openFilter === 'createdAt'}
                onToggle={() => toggleFilter('createdAt')}
                count={beginInDateFilter ? 1 : 0}
            >
                <FilterDatePicker
                    date={beginInDateFilter}
                    onChangeDate={setBeginInDateFilter}
                    time={beginInHourFilter}
                    onChangeTime={setBeginInHourFilter}
                    label={translate('createdAt')}
                    zIndex={1}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('to')}
                isOpen={openFilter === 'to'}
                onToggle={() => toggleFilter('to')}
                count={endInDateFilter ? 1 : 0}
            >
                <FilterDatePicker
                    date={endInDateFilter}
                    onChangeDate={setEndInDateFilter}
                    time={endInHourFilter}
                    onChangeTime={setEndInHourFilter}
                    label={translate('to')}
                    zIndex={1}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('priority')}
                isOpen={openFilter === 'priority'}
                onToggle={() => toggleFilter('priority')}
                count={selectedPriorityFilter !== 'all' ? 1 : 0}
            >
                <PriorityList value={selectedPriorityFilter} setValue={setSelectedPriorityFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('status')}
                isOpen={openFilter === 'status'}
                onToggle={() => toggleFilter('status')}
                count={selectedSituationFilter !== 'all' ? 1 : 0}
            >
                <StatusList value={selectedSituationFilter} setValue={setSelectedSituationFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('tag')}
                isOpen={openFilter === 'tags'}
                onToggle={() => toggleFilter('tags')}
                count={selectedTagsIds.length}
            >
                <TagsList value={selectedTagsIds} setValue={setSelectedTagsIds} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('policeReport')}
                isOpen={openFilter === 'policeReport'}
                onToggle={() => toggleFilter('policeReport')}
                count={policeReportFilter ? 1 : 0}
                searchComponent={<FilterInput
                    placeholder={translate('typeToSearch')}
                    value={policeReportFilter}
                    onChange={text => setPoliceReportFilter(text)}
                />}
            />

            <MyAppText style={styles.sectionTitle()}>
                {translate('teams')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('team')}
                isOpen={openFilter === 'teams'}
                onToggle={() => toggleFilter('teams')}
                count={selectedUnitNamesIds.length}
            >
                <DispatchUnitNamesList value={selectedUnitNamesIds} setValue={setSelectedUnitNamesIds} actingBodyId={getActingBodyId()} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('displaced')}
                isOpen={openFilter === 'displaceReasons'}
                onToggle={() => toggleFilter('displaceReasons')}
                count={selectedDisplaceReasonId.length}
            >
                <DispatchDisplacedReasonsList setValue={setSelectedDisplaceReasonId} value={selectedDisplaceReasonId} />
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('location')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('region')}
                isOpen={openFilter === 'regions'}
                onToggle={() => toggleFilter('regions')}
                count={(selectedPolygonType ? 1 : 0) + (selectedMapPolygonsIds.length > 0 ? 1 : 0)}
            >
                <DispatchRegionsList
                    polygonType={selectedPolygonType}
                    setPolygonType={setSelectedPolygonType}
                    mapPolygonsIds={selectedMapPolygonsIds}
                    setMapPolygonsIds={setSelectedMapPolygonsIds}
                    setPendingLoading={setPolygonPendingLoading}
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('findByLocation')}
                isOpen={openFilter === 'findByLocation'}
                onToggle={() => toggleFilter('findByLocation')}
                count={pin ? 1 : 0}
                searchComponent={<LocationSelector address={address} onAddressChange={setAddress} onPinChange={setPin} />}
            >
                <View style={{ gap: 10 }}>
                    {pin &&
                        <View>
                            <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                <MyAppText style={{ fontWeight: '600' }}>{translate('radius')}</MyAppText>
                                <MyAppText>{`: ${selectedRadius.toFixed(2)}km`}</MyAppText>
                            </View>
                            <Slider
                                animateTransitions
                                minimumValue={0.1}
                                maximumValue={3}
                                minimumTrackTintColor={theme.colors.neutral['400']}
                                thumbTintColor={theme.colors.neutral['400']}
                                maximumTrackTintColor={theme.colors.neutral['300']}
                                value={selectedRadius}
                                step={0.1}
                                onValueChange={(value) => setSelectedRadius(value[0])}
                            />
                            <View style={{ width: '100%', height: 450 }}>
                                <Map pinnedMarker={pin} draggable={true} mapControls={false} radius={selectedRadius} />
                            </View>
                        </View>
                    }
                </View>
            </CollapsibleFilter>

            <MyAppText style={styles.sectionTitle()}>
                {translate('classification')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('dispatchInOccurrence')}
                isOpen={openFilter === 'withDispatch'}
                onToggle={() => toggleFilter('withDispatch')}
                count={(withDispatch || withoutDispatch) ? 1 : 0}
            >
                <FilterRadioList
                    items={[
                        { label: translate('yes'), value: 'yes' },
                        { label: translate('no'), value: 'no' },
                    ]}
                    value={withDispatch ? 'yes' : withoutDispatch ? 'no' : 'all'}
                    setValue={(value) => {
                        setWithDispatch(value === 'yes');
                        setWithoutDispatch(value === 'no');
                    }}
                    clearValue='all'
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('occurrenceDocument')}
                isOpen={openFilter === 'withOccurrenceDocument'}
                onToggle={() => toggleFilter('withOccurrenceDocument')}
                count={(withOccurrenceDocument || withoutOccurrenceDocument) ? 1 : 0}
            >
                <FilterRadioList
                    items={[
                        { label: translate('withOccurrenceDocumentGenerated'), value: 'yes' },
                        { label: translate('withoutOccurrenceDocumentGenerated'), value: 'no' },
                    ]}
                    value={withOccurrenceDocument ? 'yes' : withoutOccurrenceDocument ? 'no' : 'all'}
                    setValue={(value) => {
                        setWithOccurrenceDocument(value === 'yes');
                        setWithoutOccurrenceDocument(value === 'no');
                    }}
                    clearValue='all'
                />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('typeOfIntegration')}
                isOpen={openFilter === 'type'}
                onToggle={() => toggleFilter('type')}
                count={selectedTriggerTypeFilter.length}
            >
                <TypeDropdown value={selectedTriggerTypeFilter} setValue={setSelectedTriggerTypeFilter} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('occurrenceType')}
                isOpen={openFilter === 'occurrenceType'}
                onToggle={() => toggleFilter('occurrenceType')}
                count={selectedOccurrenceTypesFilter.length}
            >
                <OccurrenceTypesDropdown value={selectedOccurrenceTypesFilter} setValue={setSelectedOccurrenceTypesFilter} zIndex={1} actingBodyId={getActingBodyId()} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('cameraTag')}
                isOpen={openFilter === 'cameraTags'}
                onToggle={() => toggleFilter('cameraTags')}
                count={selectedCameraTagsIds.length}
            >
                <CameraTagsList value={selectedCameraTagsIds} setValue={setSelectedCameraTagsIds} />
            </CollapsibleFilter>


            <MyAppText style={styles.sectionTitle()}>
                {translate('occurrenceActions')}
            </MyAppText>

            <CollapsibleFilter
                label={translate('assumedBy')}
                isOpen={openFilter === 'assumedBy'}
                onToggle={() => toggleFilter('assumedBy')}
                count={selectedAssumedBy !== 0 ? 1 : 0}
            >
                <DispatchGenericUserList value={selectedAssumedBy} setValue={setSelectedAssumedBy} actingBodyId={getActingBodyId()} label='assumedBy' />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishedBy')}
                isOpen={openFilter === 'finishedBy'}
                onToggle={() => toggleFilter('finishedBy')}
                count={selectedFinishedBy !== 0 ? 1 : 0}
            >
                <DispatchGenericUserList value={selectedFinishedBy} setValue={setSelectedFinishedBy} actingBodyId={getActingBodyId()} label='finishedBy' />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('receivedBy')}
                isOpen={openFilter === 'receivedBy'}
                onToggle={() => toggleFilter('receivedBy')}
                count={selectedReceivedBy !== 0 ? 1 : 0}
            >
                <DispatchGenericUserList value={selectedReceivedBy} setValue={setSelectedReceivedBy} actingBodyId={getActingBodyId()} label='receivedBy' />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('transferredBy')}
                isOpen={openFilter === 'transferredBy'}
                onToggle={() => toggleFilter('transferredBy')}
                count={selectedTransferredBy !== 0 ? 1 : 0}
            >
                <DispatchGenericUserList value={selectedTransferredBy} setValue={setSelectedTransferredBy} actingBodyId={getActingBodyId()} label='transferredBy' />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishReason')}
                isOpen={openFilter === 'finishReason'}
                onToggle={() => toggleFilter('finishReason')}
                count={selectedFinishReasonCategoryIds.length}
            >
                <FinishReasonDropdown value={selectedFinishReasonCategoryIds} setValue={setSelectedFinishReasonCategoryIds} actingBodyId={getActingBodyId()} zIndex={1} />
            </CollapsibleFilter>

            <CollapsibleFilter
                label={translate('finishReasonSubCategory')}
                isOpen={openFilter === 'finishReasonSubCategory'}
                onToggle={() => toggleFilter('finishReasonSubCategory')}
                count={selectedFinishReasonSubCategoryIds.length}
            >
                <FinishReasonSubCategoryDropdown
                    value={selectedFinishReasonSubCategoryIds}
                    setValue={setSelectedFinishReasonSubCategoryIds}
                    actingBodyId={getActingBodyId()}
                    finishReasonCategoryId={selectedFinishReasonCategoryIds.length === 1 ? selectedFinishReasonCategoryIds[0] : undefined}
                    zIndex={1}
                    disabled={selectedFinishReasonCategoryIds.length !== 1}
                    showTooltip={selectedFinishReasonCategoryIds.length !== 1}
                    tooltipText={translate('selectOneFinishReasonTooltip')}
                />
            </CollapsibleFilter>
        </Filters >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    filterLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    sectionTitle: (padding: number = 16) => ({
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: padding,
        textTransform: 'uppercase',
    }),
}));


export default OccurrencesFilter;
